/**
 * Course settings — schedule & details.
 *
 * Replaces Studio's "Schedule & Details" tab. Two things make this more than a form:
 *
 * CAPABILITY GATING. `course_settings` tells us which fields THIS deployment accepts.
 * A field whose flag is false is ignored or rejected on save, so rendering it would
 * invite authors to type values that silently vanish. Every gated block below is gated
 * on the flag the platform actually checks, and where a value exists but cannot be
 * edited here (enrolment end under a publisher, the verified upgrade deadline) it is
 * shown as a read-only fact with the reason — an absent field reads as a missing feature.
 *
 * WHOLE-OBJECT SAVES. The endpoint is a PUT that reads `overview` and `intro_video`
 * unguarded and treats an absent date key as "clear that date". So the draft is the
 * complete payload from the GET with edits applied, never a diff. See studio-settings.ts.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { Badge, Banner, Button, Card, Field, SelectField, Spinner } from '../../shared/ui';
import { PageHeader } from '../AdminShell';
import {
  explainFailure,
  formatInstant,
  fromLocalInput,
  getCourseDetails,
  getCourseSettings,
  languageOptions,
  normaliseDisplayBehaviour,
  resolveCourseKey,
  saveCourseDetails,
  toLocalInput,
  type CourseDetails,
  type Instructor,
} from '../../shared/lib/studio-settings';
import {
  coursePriceView,
  explainPricingFailure,
  formatMoney,
  minorUnitDigits,
  PRICE_COPY,
  pricingEditable,
  PRICING_SEAM_KEY,
  saveCoursePrice,
  type CourseMode,
} from '../../shared/lib/pricing';
import { useAdminCoursePrices, usePricingTenant } from '../../shared/lib/usePricing';
import { PricingGateNotice } from '../../shared/ui/price';
import './course-settings.css';

/** Studio's own three options, in the order it presents them. */
const DISPLAY_BEHAVIOUR: [string, string][] = [
  ['end', 'When the course ends'],
  ['end_with_date', 'On a specific date after the course ends'],
  ['early_no_info', 'As soon as each learner earns one'],
];

/**
 * The licence values the platform understands. Free text would be worse than a select:
 * `license` is parsed into a CC descriptor, and an unrecognised string renders as no
 * licence at all on the about page.
 */
const LICENCES: [string, string][] = [
  ['', 'No licence stated'],
  ['all-rights-reserved', 'All rights reserved'],
  ['creative-commons: ver=4.0 BY', 'Creative Commons BY 4.0'],
  ['creative-commons: ver=4.0 BY NC', 'Creative Commons BY-NC 4.0'],
  ['creative-commons: ver=4.0 BY NC ND', 'Creative Commons BY-NC-ND 4.0'],
  ['creative-commons: ver=4.0 BY NC SA', 'Creative Commons BY-NC-SA 4.0'],
  ['creative-commons: ver=4.0 BY ND', 'Creative Commons BY-ND 4.0'],
  ['creative-commons: ver=4.0 BY SA', 'Creative Commons BY-SA 4.0'],
];

export default function CourseSettings() {
  const params = useParams();
  const [search] = useSearchParams();
  const courseKey = resolveCourseKey(params, search);

  const queryClient = useQueryClient();

  const settings = useQuery({
    queryKey: ['course-settings', courseKey],
    queryFn: () => getCourseSettings(courseKey as string),
    enabled: Boolean(courseKey),
    staleTime: 5 * 60_000,
  });

  const details = useQuery({
    queryKey: ['course-details', courseKey],
    queryFn: () => getCourseDetails(courseKey as string),
    enabled: Boolean(courseKey),
  });

  const [draft, setDraft] = useState<CourseDetails | null>(null);
  const [baseline, setBaseline] = useState<string>('');

  // Seed once. A background refetch must not overwrite a form someone is typing in, so
  // the effect only fills an empty draft; after a save the mutation reseeds both.
  useEffect(() => {
    if (details.data && draft === null) {
      setDraft(details.data);
      setBaseline(JSON.stringify(details.data));
    }
  }, [details.data, draft]);

  const save = useMutation({
    mutationFn: (payload: CourseDetails) => saveCourseDetails(courseKey as string, payload),
    onSuccess: (saved) => {
      // Normalise the echoed payload the same way a read is: the response carries the
      // same leaked enum form of certificates_display_behavior.
      const normalised: CourseDetails = {
        ...saved,
        certificates_display_behavior: normaliseDisplayBehaviour(saved.certificates_display_behavior),
        instructor_info: { instructors: saved.instructor_info?.instructors ?? [] },
        learning_info: saved.learning_info ?? [],
        pre_requisite_courses: saved.pre_requisite_courses ?? [],
      };
      queryClient.setQueryData(['course-details', courseKey], normalised);
      setDraft(normalised);
      setBaseline(JSON.stringify(normalised));
    },
  });

  if (!courseKey) {
    return (
      <>
        <PageHeader title="Course settings" />
        <Banner tone="warning">
          <strong>No course selected.</strong> This page needs a course. Open it from the
          course list, or add the course ID to the address as{' '}
          <code>?course=course-v1:ORG+NUMBER+RUN</code>.
        </Banner>
      </>
    );
  }

  if (settings.isLoading || details.isLoading) {
    return (
      <>
        <PageHeader title="Course settings" />
        <Spinner label="Loading course settings…" />
      </>
    );
  }

  if (settings.isError || details.isError) {
    const problem = settings.error ?? details.error;
    return (
      <>
        <PageHeader title="Course settings" />
        <Banner tone="danger">
          <strong>Could not load this course&rsquo;s settings.</strong>{' '}
          {explainFailure(problem)} Nothing has been changed.{' '}
          <button
            type="button"
            className="ui-btn"
            data-variant="ghost"
            onClick={() => {
              settings.refetch();
              details.refetch();
            }}
          >
            Try again
          </button>
        </Banner>
      </>
    );
  }

  const context = settings.data;
  if (!context || !draft) {
    return (
      <>
        <PageHeader title="Course settings" />
        <Spinner label="Preparing the form…" />
      </>
    );
  }

  const set = (patch: Partial<CourseDetails>) => setDraft({ ...draft, ...patch });
  const behaviour = normaliseDisplayBehaviour(draft.certificates_display_behavior);
  const dirty = JSON.stringify(draft) !== baseline;

  /* ---- validation ---------------------------------------------------------------- */

  const errors: Record<string, string> = {};
  const warnings: string[] = [];
  const at = (iso: string | null) => (iso ? new Date(iso).getTime() : null);

  const start = at(draft.start_date);
  const end = at(draft.end_date);
  const enrolStart = at(draft.enrollment_start);
  const enrolEnd = at(draft.enrollment_end);

  if (!draft.start_date) {
    errors['start_date'] = 'A course must have a start date — learners cannot see it without one.';
  }
  if (start !== null && end !== null && end <= start) {
    errors['end_date'] = 'The end date must be after the start date.';
  }
  if (enrolStart !== null && enrolEnd !== null && enrolEnd < enrolStart) {
    errors['enrollment_end'] = 'Enrolment cannot close before it opens.';
  }
  if (behaviour === 'end_with_date' && !draft.certificate_available_date) {
    errors['certificate_available_date'] =
      'Choose the date certificates become available, or switch back to “When the course ends”.';
  }
  if (enrolEnd !== null && end !== null && enrolEnd > end) {
    warnings.push('Enrolment closes after the course ends, so late enrollees will arrive to a finished course.');
  }
  // Studio's own warning: past the upgrade deadline a learner can enrol but can no longer
  // buy the certificate-bearing track, which reads to them as a broken checkout.
  const upgradeDeadline = at(context.upgrade_deadline);
  if (enrolEnd !== null && upgradeDeadline !== null && enrolEnd > upgradeDeadline) {
    warnings.push(
      `Enrolment closes after the upgrade deadline (${formatInstant(context.upgrade_deadline)}), so learners who join late cannot upgrade.`,
    );
  }
  if (context.show_min_grade_warning) {
    warnings.push('The minimum grade for credit is below this course’s passing grade — check the grading policy.');
  }

  const blocked = Object.keys(errors).length > 0;

  const submit = () => {
    // Only 'end_with_date' may carry a date; the platform's own validator drops it for
    // the other two, and sending a stale one makes the saved state differ from the form.
    const payload: CourseDetails = {
      ...draft,
      certificates_display_behavior: behaviour,
      certificate_available_date: behaviour === 'end_with_date' ? draft.certificate_available_date : null,
    };
    save.mutate(payload);
  };

  const licenceOptions: [string, string][] =
    draft.license && !LICENCES.some(([value]) => value === draft.license)
      ? [[draft.license, `${draft.license} (current)`], ...LICENCES]
      : LICENCES;

  const languages = languageOptions(context);
  const languageChoices: [string, string][] =
    draft.language && languages.some(([code]) => code === draft.language)
      ? languages
      : [['', 'Not set'], ...languages];

  return (
    <>
      <PageHeader
        title="Schedule &amp; details"
        description={`${context.course_display_name_with_default} · ${courseKey}`}
        actions={
          <a className="ui-btn" data-variant="ghost" href={context.lms_link_for_about_page}>
            View about page
          </a>
        }
      />

      {warnings.length > 0 && (
        <Banner tone="warning">
          {warnings.map((warning) => (
            <div key={warning}>{warning}</div>
          ))}
        </Banner>
      )}

      {save.isError && (
        <Banner tone="danger">
          <strong>These settings were not saved.</strong> {explainFailure(save.error)} Your
          edits are still on screen, so you can correct them and save again.
        </Banner>
      )}
      {save.isSuccess && !dirty && (
        <Banner tone="success">
          <strong>Saved.</strong> Learners see the new schedule and details immediately.
        </Banner>
      )}

      <form
        className="cs-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!blocked) submit();
        }}
      >
        <Card title="Schedule">
          <div className="cs-grid">
            <Field
              label="Course start"
              name="start_date"
              type="datetime-local"
              value={toLocalInput(draft.start_date)}
              onChange={(event) => set({ start_date: fromLocalInput(event.target.value) })}
              error={errors['start_date']}
              hint="When the course opens to enrolled learners."
            />
            <Field
              label="Course end"
              name="end_date"
              type="datetime-local"
              value={toLocalInput(draft.end_date)}
              onChange={(event) => set({ end_date: fromLocalInput(event.target.value) })}
              error={errors['end_date']}
              hint="Leave empty for a course with no end."
            />
            <Field
              label="Enrolment opens"
              name="enrollment_start"
              type="datetime-local"
              value={toLocalInput(draft.enrollment_start)}
              onChange={(event) => set({ enrollment_start: fromLocalInput(event.target.value) })}
            />

            {/* Gated: under a publisher-managed catalogue the platform owns enrolment
                end, and a value typed here would be discarded on save. */}
            {context.enrollment_end_editable ? (
              <Field
                label="Enrolment closes"
                name="enrollment_end"
                type="datetime-local"
                value={toLocalInput(draft.enrollment_end)}
                onChange={(event) => set({ enrollment_end: fromLocalInput(event.target.value) })}
                error={errors['enrollment_end']}
              />
            ) : (
              <dl className="cs-facts">
                <div>
                  <dt>Enrolment closes</dt>
                  <dd>{formatInstant(draft.enrollment_end)}</dd>
                </div>
              </dl>
            )}
          </div>

          {!context.enrollment_end_editable && (
            <p className="cs-note">
              Enrolment end is managed outside Studio for this organisation, so it is shown
              here but cannot be changed. Ask a platform administrator to move it.
            </p>
          )}

          <label className="cs-check">
            <input
              type="checkbox"
              checked={draft.self_paced}
              onChange={(event) => set({ self_paced: event.target.checked })}
            />
            <span>
              Self-paced
              <small>
                Learners work through the material on their own schedule and subsection due
                dates are ignored. Pacing can only be changed before the course starts.
              </small>
            </span>
          </label>

          {/* Gated: certificate timing only exists where the deployment can show it —
              elsewhere the field has no effect on when certificates appear. */}
          {context.can_show_certificate_available_date_field && (
            <>
              <span className="cs-legend">Certificate availability</span>
              <div className="cs-grid">
                <SelectField
                  label="Certificates become available"
                  name="certificates_display_behavior"
                  value={behaviour}
                  onChange={(event) => set({ certificates_display_behavior: event.target.value })}
                  options={DISPLAY_BEHAVIOUR}
                />
                {behaviour === 'end_with_date' && (
                  <Field
                    label="Certificate available date"
                    name="certificate_available_date"
                    type="datetime-local"
                    value={toLocalInput(draft.certificate_available_date)}
                    onChange={(event) =>
                      set({ certificate_available_date: fromLocalInput(event.target.value) })
                    }
                    error={errors['certificate_available_date']}
                  />
                )}
              </div>
            </>
          )}

          {context.upgrade_deadline && (
            <dl className="cs-facts">
              <div>
                <dt>Upgrade deadline</dt>
                <dd>{formatInstant(context.upgrade_deadline)}</dd>
              </div>
            </dl>
          )}
        </Card>

        {/* Between Schedule and Course details, so an author sees the price beside the dates
            that decide whether anybody can buy it. Its OWN card and its own save — see the
            component's notes for why it cannot join the draft above. */}
        <PricingCard courseKey={courseKey} enrollmentEnd={draft.enrollment_end} />

        <Card title="Course details">
          <dl className="cs-facts">
            <div>
              <dt>Course name</dt>
              <dd>{context.course_display_name_with_default}</dd>
            </div>
            <div>
              <dt>Organisation</dt>
              <dd>{draft.org}</dd>
            </div>
            <div>
              <dt>Number</dt>
              <dd>{draft.course_id}</dd>
            </div>
            <div>
              <dt>Run</dt>
              <dd>{draft.run}</dd>
            </div>
          </dl>

          <div className="cs-grid">
            <SelectField
              label="Course language"
              name="language"
              value={draft.language ?? ''}
              onChange={(event) => set({ language: event.target.value || null })}
              options={languageChoices}
            />
            <Field
              label="Effort"
              name="effort"
              value={draft.effort ?? ''}
              onChange={(event) => set({ effort: event.target.value })}
              hint="Hours per week, e.g. 5–6 hours."
            />
            {/* Gated on LICENSING: without the feature the value is stored but never shown. */}
            {context.licensing_enabled && (
              <SelectField
                label="Content licence"
                name="license"
                value={draft.license ?? ''}
                onChange={(event) => set({ license: event.target.value || null })}
                options={licenceOptions}
              />
            )}
            <Field
              label="Introduction video"
              name="intro_video"
              value={draft.intro_video ?? ''}
              onChange={(event) => set({ intro_video: event.target.value || null })}
              hint="YouTube video ID only, e.g. dQw4w9WgXcQ."
            />
          </div>

          {/* Gated: some deployments make the short description read-only because a
              catalogue system owns it. */}
          {context.short_description_editable && (
            <div className="cs-area">
              <label htmlFor="short_description">Short description</label>
              <textarea
                id="short_description"
                value={draft.short_description}
                maxLength={150}
                rows={2}
                onChange={(event) => set({ short_description: event.target.value })}
              />
              <p className="ui-field__hint">
                Appears in course listings and search results. {150 - draft.short_description.length}{' '}
                characters left.
              </p>
            </div>
          )}

          {/* Gated on ENABLE_EXTENDED_COURSE_DETAILS. */}
          {context.enable_extended_course_details && (
            <>
              <span className="cs-legend">Extended details</span>
              <div className="cs-grid">
                <Field
                  label="Title"
                  name="title"
                  value={draft.title}
                  onChange={(event) => set({ title: event.target.value })}
                  hint="Marketing title, if it differs from the course name."
                />
                <Field
                  label="Subtitle"
                  name="subtitle"
                  value={draft.subtitle}
                  onChange={(event) => set({ subtitle: event.target.value })}
                />
                <Field
                  label="Duration"
                  name="duration"
                  value={draft.duration}
                  onChange={(event) => set({ duration: event.target.value })}
                  hint="e.g. 6 weeks."
                />
              </div>
              <div className="cs-area">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  rows={3}
                  value={draft.description}
                  onChange={(event) => set({ description: event.target.value })}
                />
              </div>
              <div className="cs-area">
                <label htmlFor="learning_info">What learners will learn</label>
                <textarea
                  id="learning_info"
                  rows={4}
                  value={draft.learning_info.join('\n')}
                  onChange={(event) =>
                    // Stored as a list; one line per outcome is the least fiddly way to
                    // edit a list of short strings.
                    set({ learning_info: event.target.value.split('\n').filter((line) => line.trim() !== '') })
                  }
                />
                <p className="ui-field__hint">One outcome per line.</p>
              </div>

              <Instructors
                instructors={draft.instructor_info.instructors}
                onChange={(instructors) => set({ instructor_info: { instructors } })}
              />
            </>
          )}
        </Card>

        {/* Gated: with a marketing site in front, the about page is not served from here,
            so editing this HTML would change nothing a visitor sees. */}
        {context.about_page_editable ? (
          <Card title="About page">
            {context.marketing_enabled && (
              <Banner tone="info">
                A marketing site fronts this catalogue. These fields still feed the course
                API, but the public page is assembled there.
              </Banner>
            )}
            <div className="cs-area">
              <label htmlFor="overview">Course overview</label>
              <textarea
                id="overview"
                data-mono
                rows={12}
                value={draft.overview}
                onChange={(event) => set({ overview: event.target.value })}
              />
              <p className="ui-field__hint">
                HTML. Studio&rsquo;s template uses sections for the description,
                requirements, staff and FAQ.
              </p>
            </div>

            {/* Gated on the ENABLE_COURSE_ABOUT_SIDEBAR_HTML waffle flag. */}
            {context.sidebar_html_enabled && (
              <div className="cs-area">
                <label htmlFor="about_sidebar_html">About page sidebar</label>
                <textarea
                  id="about_sidebar_html"
                  data-mono
                  rows={6}
                  value={draft.about_sidebar_html ?? ''}
                  onChange={(event) => set({ about_sidebar_html: event.target.value })}
                />
                <p className="ui-field__hint">HTML shown beside the overview.</p>
              </div>
            )}
          </Card>
        ) : (
          <Card title="About page">
            <p className="cs-note">
              The about page for this organisation is published from its catalogue system,
              so it cannot be edited here.{' '}
              <a href={context.lms_link_for_about_page}>See the current page</a>.
            </p>
          </Card>
        )}

        {(context.is_prerequisite_courses_enabled ||
          context.is_entrance_exams_enabled ||
          (context.credit_eligibility_enabled && context.is_credit_course)) && (
          <Card title="Requirements">
            {/* Gated: prerequisites are milestone-backed. With the feature off, keys sent
                here raise "Invalid prerequisite course key" or are dropped. */}
            {context.is_prerequisite_courses_enabled && (
              <div className="ui-field cs-multi">
                <label htmlFor="pre_requisite_courses">Prerequisite courses</label>
                <select
                  id="pre_requisite_courses"
                  multiple
                  size={5}
                  value={draft.pre_requisite_courses}
                  onChange={(event) =>
                    set({
                      pre_requisite_courses: Array.from(
                        event.target.selectedOptions,
                        (option) => option.value,
                      ),
                    })
                  }
                >
                  {(context.possible_pre_requisite_courses ?? []).map((course) => (
                    <option key={course.course_key} value={course.course_key}>
                      {course.display_name} ({course.number} {course.run})
                    </option>
                  ))}
                </select>
                <p className="ui-field__hint">
                  {(context.possible_pre_requisite_courses ?? []).length === 0
                    ? 'No other courses are available to require yet.'
                    : 'Learners must complete these before they can enrol. Ctrl/Cmd-click to select more than one, or to clear a selection.'}
                </p>
              </div>
            )}

            {/* Gated on the ENTRANCE_EXAMS toggle. */}
            {context.is_entrance_exams_enabled && (
              <>
                <label className="cs-check">
                  <input
                    type="checkbox"
                    checked={draft.entrance_exam_enabled === 'true'}
                    // A STRING, not a boolean — the platform compares against 'true'.
                    onChange={(event) =>
                      set({ entrance_exam_enabled: event.target.checked ? 'true' : '' })
                    }
                  />
                  <span>
                    Require an entrance exam
                    <small>
                      Adds a graded section learners must pass before the rest of the course
                      unlocks. Saving this creates or removes that section.
                    </small>
                  </span>
                </label>
                {draft.entrance_exam_enabled === 'true' && (
                  <div className="cs-grid">
                    <Field
                      label="Minimum score to pass (%)"
                      name="entrance_exam_minimum_score_pct"
                      type="number"
                      min={1}
                      max={100}
                      value={draft.entrance_exam_minimum_score_pct}
                      onChange={(event) =>
                        set({ entrance_exam_minimum_score_pct: event.target.value })
                      }
                    />
                  </div>
                )}
              </>
            )}

            {context.credit_eligibility_enabled && context.is_credit_course && (
              <Banner tone="info">
                <strong>This is a credit course.</strong> Its credit requirements
                {Object.keys(context.credit_requirements ?? {}).length > 0
                  ? ` (${Object.keys(context.credit_requirements ?? {}).join(', ')}) `
                  : ' '}
                are set by the credit provider. The minimum grade for credit is on the
                grading page.
              </Banner>
            )}
          </Card>
        )}

        <div className="cs-bar">
          <span className="cs-bar__state" data-dirty={dirty ? 'true' : 'false'}>
            {dirty ? 'Unsaved changes' : 'All changes saved'}
          </span>
          {blocked && (
            <Badge tone="danger">
              {Object.keys(errors).length} field
              {Object.keys(errors).length === 1 ? '' : 's'} need attention
            </Badge>
          )}
          <div className="cs-bar__end">
            {dirty && (
              <Button
                variant="ghost"
                type="button"
                onClick={() => {
                  if (details.data) {
                    setDraft(details.data);
                    setBaseline(JSON.stringify(details.data));
                  }
                }}
              >
                Discard changes
              </Button>
            )}
            <Button
              type="submit"
              variant="primary"
              busy={save.isPending}
              disabled={!dirty || blocked}
            >
              Save changes
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}

/**
 * Instructor list.
 *
 * Stored as `instructor_info.instructors`, a list of objects — so it is edited as rows
 * rather than text. Rows are keyed by index because the platform gives instructors no id.
 */
function Instructors({
  instructors,
  onChange,
}: {
  instructors: Instructor[];
  onChange: (instructors: Instructor[]) => void;
}) {
  const update = (index: number, patch: Partial<Instructor>) =>
    onChange(instructors.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <>
      <span className="cs-legend">Instructors</span>
      {instructors.length === 0 && (
        <p className="cs-note">No instructors listed. Add one to show them on the about page.</p>
      )}
      {instructors.map((instructor, index) => (
        <div className="cs-grid" key={index}>
          <Field
            label="Name"
            name={`instructor-name-${index}`}
            value={instructor.name ?? ''}
            onChange={(event) => update(index, { name: event.target.value })}
          />
          <Field
            label="Title"
            name={`instructor-title-${index}`}
            value={instructor.title ?? ''}
            onChange={(event) => update(index, { title: event.target.value })}
          />
          <Field
            label="Organisation"
            name={`instructor-org-${index}`}
            value={instructor.organization ?? ''}
            onChange={(event) => update(index, { organization: event.target.value })}
          />
          <div className="cs-inline">
            <Button
              variant="danger"
              type="button"
              onClick={() => onChange(instructors.filter((_, i) => i !== index))}
            >
              Remove
            </Button>
          </div>
        </div>
      ))}
      <div className="cs-inline">
        <Button
          type="button"
          onClick={() => onChange([...instructors, { name: '', title: '', organization: '', bio: '', image: '' }])}
        >
          Add instructor
        </Button>
      </div>
    </>
  );
}

/* ================================================================== pricing ====== */

/**
 * What this course costs (spec R6.1).
 *
 * ITS OWN CARD AND ITS OWN MUTATION, AND THAT IS THE WHOLE REASON IT IS A SEPARATE
 * COMPONENT. The form above saves by WHOLE-OBJECT PUT to Studio's own endpoint, which reads
 * only the fields it knows — see this file's header: "the draft is the complete payload from
 * the GET with edits applied, never a diff". A price key added to that draft would be sent
 * to Studio, dropped on the floor, and the form would report a save that never happened
 * (spec Assumption 7). So this card holds its own state and writes to
 * `PUT /api/pricing/v1/courses/{id}`, and nothing it owns ever touches `draft`.
 *
 * THE AUTHOR SETS THE NET AND IS SHOWN WHAT THE LEARNER PAYS. `amount_minor` is the
 * academy's own take; the processing markup is added server-side. An author who does not
 * know what the learner pays cannot price anything, so the total is stated under the field
 * and it is the SERVER's number after a save — `previewPathwayTotals`-style local arithmetic
 * is not used here, because a course price needs no live preview to be legible.
 *
 * THE CURRENCY IS A STATIC PREFIX, NEVER A SELECT (spec Assumption 1). It comes from the
 * tenant's payment profile. A course priced in a currency the academy's Stripe account does
 * not hold is refused by the server, so offering the choice would be offering a mistake.
 */
function PricingCard({
  courseKey,
  enrollmentEnd,
}: {
  courseKey: string;
  /** From the draft above, read-only: a priced course nobody can join is worth a warning. */
  enrollmentEnd: string | null;
}) {
  const queryClient = useQueryClient();

  // The gate and the fee policy. Needed even when this course has no price row yet, which is
  // why it is its own tiny query and not a field on the price document.
  const tenantSeam = usePricingTenant();
  const tenant = tenantSeam?.tenant ?? null;

  // The ADMIN read, so a course taken off sale still shows the author its price.
  const priceSeam = useAdminCoursePrices([courseKey]);
  const saved = coursePriceView(priceSeam, courseKey);

  const editable = pricingEditable(tenant);
  const currency = tenant?.currency ?? saved.currency ?? '';
  const digits = currency ? minorUnitDigits(currency) : 2;

  const [mode, setMode] = useState<CourseMode | null>(null);
  const [amount, setAmount] = useState('');

  /**
   * Seed the form from the saved row ONCE IT ARRIVES, and never again.
   *
   * `mode === null` is "the author has not touched this yet", which is what makes this safe:
   * without it, the 5-minute refetch of the price document would overwrite whatever the
   * author was halfway through typing. The same reason the schedule form above seeds its
   * draft from the GET rather than binding to it.
   */
  useEffect(() => {
    if (mode !== null || saved.mode === 'unknown') return;
    setMode(saved.mode === 'free' ? 'free' : 'paid');
    if (saved.baseMinor !== null) setAmount((saved.baseMinor / 10 ** digits).toFixed(digits));
  }, [saved.mode, saved.baseMinor, digits, mode]);

  const chosen = mode ?? (saved.mode === 'paid' ? 'paid' : 'free');

  /**
   * The typed amount as minor units, or null when it is not a number at all.
   *
   * `Math.round` on the scaled value rather than a parse of the decimal string: the input is
   * a decimal the author typed and the wire takes integers, and this is the one conversion
   * between them. It is rounding a UI value into the unit the API uses, not arithmetic on a
   * price — every number that is compared or displayed downstream is already minor units.
   */
  const typed = amount.trim();
  const amountMinor =
    typed === '' || !Number.isFinite(Number(typed))
      ? null
      : Math.round(Number(typed) * 10 ** digits);

  const floor = tenant?.minimumChargeMinor ?? 0;

  let error: string | null = null;
  if (chosen === 'paid') {
    if (amountMinor === null || amountMinor <= 0) {
      error = 'Enter the amount this course costs, as a number greater than zero.';
    } else if (amountMinor < floor) {
      error = `This academy's payment account cannot charge less than ${formatMoney(floor, currency)}.`;
    }
  }

  // A priced course whose enrolment has already closed is a product nobody can buy. A
  // WARNING and not an error: the author may be pricing it before reopening enrolment, and
  // refusing the save would make that impossible.
  const closed =
    chosen === 'paid' && enrollmentEnd !== null && Date.parse(enrollmentEnd) < Date.now();

  const save = useMutation({
    mutationFn: () =>
      saveCoursePrice(courseKey, {
        mode: chosen,
        // Null when free: the row is cleared rather than kept at an amount nothing reads.
        amount_minor: chosen === 'paid' ? amountMinor : null,
      }),
    // Every surface that shows a price reads under the one root key, so this is what makes
    // the admin table, the catalogue card and the course page agree again after a save.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: PRICING_SEAM_KEY }),
  });

  return (
    <Card title="Pricing">
      {/* The gate stays VISIBLE and the controls stay PRESENT and disabled (R4.2): an absent
          section reads as a missing feature, and the reader cannot tell "not yet" from
          "never". `PricingGateNotice` renders nothing when the gate holds. */}
      <PricingGateNotice tenant={tenant} />

      <SelectField
        label="Price"
        name="price_mode"
        value={chosen}
        disabled={!editable}
        options={[
          ['free', 'Free'],
          ['paid', 'Paid'],
        ]}
        onChange={(event) => setMode(event.target.value === 'paid' ? 'paid' : 'free')}
      />

      {chosen === 'paid' && (
        <>
          <Field
            label={currency ? `Amount (${currency})` : 'Amount'}
            name="price_amount"
            type="number"
            inputMode="decimal"
            min="0"
            step={(1 / 10 ** digits).toFixed(digits)}
            value={amount}
            disabled={!editable}
            onChange={(event) => setAmount(event.target.value)}
            error={error ?? undefined}
            hint="What this academy keeps. Payment processing is added on top."
          />

          {/*
            WHAT THE LEARNER ACTUALLY PAYS (R6.1), from the SAVED row.

            Shown only once the server has priced it, and deliberately not recomputed from
            the box as the author types: the fee is the server's arithmetic, and a local
            preview that disagreed with it by one minor unit would be a second rounding rule
            on one product. Until the save lands, the field's own hint says the markup is
            added on top; after it, this line names both numbers.
          */}
          {saved.mode === 'paid' && saved.totalMinor !== null && saved.currency && (
            <p className="cs-note">
              Learners are charged {formatMoney(saved.totalMinor, saved.currency)}
              {saved.feeMinor !== null && (
                <>, which includes {formatMoney(saved.feeMinor, saved.currency)} payment
                processing</>
              )}
              .
            </p>
          )}
        </>
      )}

      {closed && (
        <Banner tone="warning">
          Enrolment for this course has closed, so nobody can buy it while that is true.
          Reopen enrolment above, or leave it priced and ready.
        </Banner>
      )}

      {saved.mode === 'unknown' && saved.reason && <Banner tone="warning">{saved.reason}</Banner>}

      {/*
        NOT ON STRIPE YET — the author's half of `price_synced`, and the only place it is now
        said out loud.

        It used to be shown to the LEARNER, as a disabled Buy button with "this price is still
        being set up", which is what made the payments app's checkout self-heal unreachable: the
        repair runs at checkout and nothing could get a course out of the unsynced state. So the
        learner now sees a normal price and the sale works, and the fact lands here instead —
        where the reader can act on it, because a row that stays in this state means the
        save-time sync is failing and there is a `state=error` catalogue link and an LMS log line
        waiting to be read.

        `info`, not `warning`: nothing is wrong for the learner. It becomes wrong only if it
        persists, which is what the sentence says.
      */}
      {saved.mode === 'paid' && saved.syncPending && (
        <Banner tone="info">{PRICE_COPY.notSynced}</Banner>
      )}

      {save.isError && <Banner tone="danger">{explainPricingFailure(save.error)}</Banner>}
      {save.isSuccess && !save.isPending && (
        <Banner tone="success">Price saved. Every page showing this course now agrees.</Banner>
      )}

      <div className="cs-inline">
        <Button
          variant="primary"
          type="button"
          busy={save.isPending}
          disabled={!editable || error !== null}
          onClick={() => save.mutate()}
        >
          Save price
        </Button>
      </div>
    </Card>
  );
}
